import * as fromTasks from '../actions/task.actions';
import { GematikTask, GematikTaskCachedDto } from '../../models/gematik-task.model';

export const OPEN_FEATURE_KEY = 'openTasks';
export const COMPLETED_FEATURE_KEY = 'completedTasks';

export interface TaskState {
  groupTasks: {
    entities: { [id: string]: GematikTask };
  };
  myTasks: {
    entities: { [id: string]: GematikTask };
  };

  loading: boolean;
  loaded: boolean;
  error: boolean;
}

export const initialState: TaskState = {
  groupTasks: {
    entities: {},
  },
  myTasks: {
    entities: {},
  },
  loading: false,
  loaded: false,
  error: false,
};

export function taskOpenReducer(state = initialState, action: fromTasks.TaskActions): TaskState {
  switch (action.type) {
    case fromTasks.LOAD_OPEN_TASKS:
    case fromTasks.LOAD_ALL_OPEN_TASKS: {
      return {
        ...state,
        loading: true,
        error: false,
      };
    }
    case fromTasks.LOAD_ALL_OPEN_TASKS_SUCCESS: {
      const tasks = action.payload;
      const groupTaskEntities = tasks.groupTasks.reduce(
        (entities: { [id: string]: GematikTask }, task: GematikTask) => {
          return {
            ...entities,
            [task.id]: {
              ...task,
              role: task.assigneeName ? `${task.role} (${task.assigneeName})` : task.role,
            },
          };
        },
        {
          ...state.groupTasks.entities,
        },
      );
      const myTaskEntities = tasks.myTasks.reduce(
        (entities: { [id: string]: GematikTask }, task: GematikTask) => {
          return {
            ...entities,
            [task.id]: task,
          };
        },
        {
          ...state.myTasks.entities,
        },
      );
      return {
        ...state,
        loading: false,
        loaded: true,
        groupTasks: {
          entities: groupTaskEntities,
        },
        myTasks: {
          entities: myTaskEntities,
        },
      };
    }
    case fromTasks.LOAD_OPEN_TASKS_FAIL:
    case fromTasks.LOAD_ALL_OPEN_TASKS_FAIL: {
      return {
        ...state,
        loading: false,
        loaded: false,
        error: true,
      };
    }
    case fromTasks.LOAD_OPEN_TASKS_SUCCESS: {
      const tasks = action.payload;

      // remove
      const myTasks = Object.entries(state.myTasks.entities)
        .filter(entity => entity[1].backendUrl !== action.backendUrl)
        .map(entity => entity[1]);

      const groupTasks = Object.entries(state.groupTasks.entities)
        .filter(entity => entity[1].backendUrl !== action.backendUrl)
        .map(entity => entity[1]);

      // add
      myTasks.push(...action.payload.myTasks);
      groupTasks.push(...action.payload.groupTasks);

      // reduce
      const myTaskEntities = myTasks.reduce((entities: { [id: string]: GematikTask }, task: GematikTask) => {
        return {
          ...entities,
          [task.id]: task,
        };
      }, {});

      const groupTaskEntities = groupTasks.reduce((entities: { [id: string]: GematikTask }, task: GematikTask) => {
        return {
          ...entities,
          [task.id]: {
            ...task,
            role: task.assigneeName ? `${task.role} (${task.assigneeName})` : task.role,
          },
        };
      }, {});

      return {
        ...state,
        loading: false,
        loaded: true,
        groupTasks: {
          entities: groupTaskEntities,
        },
        myTasks: {
          entities: myTaskEntities,
        },
      };
    }

    case fromTasks.LOAD_OPEN_TASK: {
      return {
        ...state,
        loading: true,
        error: false,
      };
    }
    case fromTasks.LOAD_OPEN_TASK_SUCCESS: {
      const task = action.payload;
      const eventName = action.eventName;
      if (!task?.id) {
        return state;
      }

      if (eventName === 'assignment') {
        const { [task.id]: _removed, ...remainingMyTasks } = state.myTasks.entities;
        const existingGroupTask = state.groupTasks.entities[task.id];
        const groupTasksEntities = existingGroupTask
          ? {
              ...state.groupTasks.entities,
              [task.id]: {
                ...existingGroupTask,
                role: task.assigneeName ? `${task.role} (${task.assigneeName})` : task.role,
              },
            }
          : state.groupTasks.entities;
        return {
          ...state,
          loading: false,
          myTasks: {
            entities:
              task.assignee && task.taskOwner
                ? { ...state.myTasks.entities, [task.id]: task as unknown as GematikTaskCachedDto }
                : remainingMyTasks,
          },
          groupTasks: { entities: groupTasksEntities },
        };
      }

      const myTasksEntities = task.taskOwner
        ? { ...state.myTasks.entities, [task.id]: task as unknown as GematikTaskCachedDto }
        : state.myTasks.entities;
      const groupTasksEntities = !task.taskOwner
        ? { ...state.groupTasks.entities, [task.id]: task as unknown as GematikTaskCachedDto }
        : state.groupTasks.entities;
      return {
        ...state,
        loading: false,
        myTasks: { entities: myTasksEntities },
        groupTasks: { entities: groupTasksEntities },
      };
    }
    case fromTasks.LOAD_OPEN_TASK_FAIL: {
      return {
        ...state,
        loading: false,
        loaded: false,
      };
    }

    case fromTasks.TASK_COMPLETED: {
      const { [action.taskId]: _my, ...remainingMyTasks } = state.myTasks.entities;
      const { [action.taskId]: _sub, ...remainingGroupTasks } = state.groupTasks.entities;
      return {
        ...state,
        myTasks: { entities: remainingMyTasks },
        groupTasks: { entities: remainingGroupTasks },
      };
    }
  }
  return state;
}

export const getGroupTaskEntities = (state: TaskState) => state.groupTasks.entities;
export const getMyTaskEntities = (state: TaskState) => state.myTasks.entities;

export function taskCompletedReducer(state = initialState, action: fromTasks.TaskActions): TaskState {
  switch (action.type) {
    case fromTasks.LOAD_GROUP_COMPLETED_TASKS:
    case fromTasks.LOAD_MY_COMPLETED_TASKS: {
      return {
        ...state,
        loading: true,
        error: false,
      };
    }
    case fromTasks.LOAD_MY_COMPLETED_TASKS_SUCCESS: {
      const tasks = action.payload;
      const myTaskEntities = tasks.myTasks.reduce(
        (entities: { [id: string]: GematikTask }, task: GematikTask) => {
          return {
            ...entities,
            [task.id]: task,
          };
        },
        {
          ...state.myTasks.entities,
        },
      );
      return {
        ...state,
        loading: false,
        loaded: true,
        myTasks: {
          entities: myTaskEntities,
        },
      };
    }
    case fromTasks.LOAD_GROUP_COMPLETED_TASKS_SUCCESS: {
      const tasks = action.payload;
      const groupTaskEntities = tasks.groupTasks.reduce(
        (entities: { [id: string]: GematikTask }, task: GematikTask) => {
          return {
            ...entities,
            [task.id]: task,
          };
        },
        {
          ...state.myTasks.entities,
        },
      );
      return {
        ...state,
        loading: false,
        loaded: true,
        groupTasks: {
          entities: groupTaskEntities,
        },
      };
    }
    case fromTasks.LOAD_GROUP_COMPLETED_TASKS_FAIL:
    case fromTasks.LOAD_MY_COMPLETED_TASKS_FAIL: {
      return {
        ...state,
        loading: false,
        loaded: false,
        error: true,
      };
    }
  }
  return state;
}
